import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  DeleteRequest,
  GetRequest,
  PostRequest,
  PutRequest,
} from "../../Utils/hooks/ApiHandler";
import { TableSkeleton } from "../../Utils/skeletonLoader/TableSkeleton";  // skeleton loader
import { TableComponent } from "../TableComponent";
import useLocalStorageState from "use-local-storage-state";
import { useNavigate } from "react-router-dom";
import { useGetRequest, usePostRequest } from "../../Utils/hooks/QueryHandler";
import { AuthorizationCard } from "../../pages/AuthorizationCard";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconInfoCircle } from "@tabler/icons-react";
import Tooltip from "@mui/material/Tooltip";


const schema = z.object({
  roleName: z.string().min(1, "Role Name is required"),
  description: z.string().min(1, "Description is required"),
});


export const RoleManagement = () => {
  // const { register, handleSubmit, reset, setValue } = useForm();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      status: "Active",
    },
  });
  const [roles, setRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const [customer, setCustomer] = useLocalStorageState("customer");
  const [currRoleId, setCurrRoleId] = useState()
  const [isDeleting, setIsDeleting] = useState(false)
  const [isGlobalMasking, setIsGlobalMasking] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const customer_id = customer?.customer_details?.customer_id;
  const user_id = customer?.customer_details?.id;
  const token = customer?.token?.authorization;
  const rf = customer?.token?.authentication;
  const headers = {
    Authentication: rf,
    Authorization: token,
  };
  const extras = { customer, setCustomer, rf, navigate };
  const {
    data: rolesData,
    error,
    refetch: refetchRoles,
    isLoading,
    isError,
  } = useGetRequest(
    `/users/Roles/getRoles/${customer_id}`, "getRoles", headers, extras, {}
  );


  // permission for user management
  const permission = customer.customer_details?.permission
  const settingPermision = permission?.filter(c => c.module_name === "setting")
  const user_type = Number(customer.customer_details?.user_type)
  const roleGrant = {
    isView: user_type !== 1 ? Boolean(Number(settingPermision?.find(c => c.feature_name?.toLowerCase()?.split(" ")?.join("_") === "role_management")?.view)) : true,
    isCreate: user_type !== 1 ? Boolean(Number(settingPermision?.find(c => c.feature_name?.toLowerCase()?.split(" ")?.join("_") === "role_management")?.create)) : true,
    isEdit: user_type !== 1 ? Boolean(Number(settingPermision?.find(c => c.feature_name?.toLowerCase()?.split(" ")?.join("_") === "role_management")?.edit)) : true,
    isDelete: user_type !== 1 ? Boolean(Number(settingPermision?.find(c => c.feature_name?.toLowerCase()?.split(" ")?.join("_") === "role_management")?.delete)) : true
  }

  useEffect(() => {
    if (rolesData?.status === "success") {
      setRoles(rolesData.data);
    } else if (isError) {
      console.error("Error fetching roles:", error);
    }
  }, [rolesData, isError]);

  const { mutate: addRole, isLoading: isSubmitting } = usePostRequest(
    "/users/Roles/addRole",
    false,
    { headers },
    extras
  );


  //  onSubmit function for creating and updationg user role
  const onSubmit = async (data) => {
    const roleName = data.roleName?.trim();
    const description = data.description;
    // Common payload base
    const payloadBase = {
      customer_id,
      role: roleName,
      description: description,
    };

    try {
      if (editIndex !== null) {
        setIsUpdating(true);
        const payload = {
          ...payloadBase,
          role_id: currRoleId,
          user_id: user_id,
          is_hashed: isGlobalMasking ? "yes" : "no"
        };
        const res = await PutRequest(
          "/users/Roles/updateRole",
          payload,
          { headers },
          extras
        );

        console.log("Update response:", res);

        if (!res || res.success === false) {
          toast.error(res?.message || "Update failed");
          return;
        }

        toast.success(res.message || "Role updated successfully");
        await refetchRoles();
        setShowModal(false);
        reset();
        setCurrRoleId(null);
        setEditIndex(null);
        setIsGlobalMasking(true);

      } else {
        // ADD ROLE FUNCTION
        const payload = {
          ...payloadBase,
          user_id: user_id,
          is_hashed: isGlobalMasking ? "yes" : "no",
        };
        addRole(payload, {
          onSuccess: (res) => {
            console.log("Add Role Response:", res);
            if (res?.status === "success") {
              // handleSave(res.data?.role_id);
              toast.success(res.message || "Role added successfully");
              // trigger refetch to fetch all roles again for displaying on UI after successfully adding the role
              refetchRoles();
              setShowModal(false);
              reset();
              setEditIndex(null);
            } else {
              toast.error(res.message || "Failed to add role");
            }
          },
          onError: (err) => {
            toast.error(err?.message || "Add role request failed");
          },
        });
        return;
      }
    } catch (error) {
      toast.error(error?.message || "Error saving role");
    }finally {
    setIsUpdating(false);  
  }
  };

  // handle edit (this fucntion gives me a data of a specific role)
  const handleEdit = async (index) => {
    try {
      const roleId = roles[index].id;
      setCurrRoleId(roleId)
      const res = await GetRequest(
        `/users/Roles/getRoleById/${roleId}`,
        { headers },
        extras
      );
      if (res?.status === "success" && Array.isArray(res.data)) {
        if (res.authorization != "") {
          setCustomer((customer.token.authorization = res.authorization));
        }
      }
      const role = res.data[0];
      // if not role throw new error
      if (!role) throw new Error("Role not found from getRoleById");
      setValue("roleName", role.role_title);
      setValue("description", role.description);
      setIsGlobalMasking(role.is_hashed === "yes");
      setValue("customer_id", role.customer_id);

      setEditIndex(index);
      setShowModal(true)
      // await fetchMaskingSettings(roleId);
    } catch (error) {
      // toast error
      toast.error("Error Editing Role !");
    }
  };

  // delete function for delete a user role
  const confirmDelete = async () => {
    setIsDeleting(true)
    try {
      const roleId = roles[deleteIndex].id;
      const res = await DeleteRequest(
        `/users/Roles/deleteRole/${roleId}`,
        { headers },
        extras
      );
      if (res?.status === 'success') {
        setRoles(roles.filter((_, i) => i !== deleteIndex));
        setShowDeleteModal(false);
        setDeleteIndex(null);
        setIsDeleting(false)
        // toast success
        toast.success(res.message);
      } else {
        toast.error(res.message)
        setIsDeleting(false)
      }
    } catch (error) {
      //toast error
      toast.error("Delete Failed");
      setIsDeleting(false)
    }
  };

  // Filter roles based on searchTerm (case-insensitive)
  const filteredRoles = roles.filter((role) =>
    role.role_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container">
      <div className="mb-3 d-flex justify-content-between align-items-end">
        <div style={{ lineHeight: "0.8rem" }}>
          <h6
            style={{ fontWeight: "500", color: "black", fontSize: "1.0rem" }}
          >
            Users Permission
          </h6>
          <p className="text-muted mb-0 small" style={{ color: "#c7c7c7" }}>
            You can manage Logo, Receipt, Admission, etc. information LMS system
            from here.
          </p>
        </div>

      </div>
      <hr
        className="mt-2 mb-4"
        style={{
          borderTop: "0.5px solid #6c757d",
          opacity: "0.3",
        }}
      />
      {
        roleGrant?.isView ?
          <div className="card p-3">
            <div className="d-flex justify-content-between align-items-center gap-1 mb-1">
              {/* <h5 className="">Role Setup</h5> */}
              <div style={{ maxWidth: "65%" }}>
                <h6 className="fw-semibold mb-1">Role Setup</h6>
                <p className="text-muted small mb-2 mb-md-0">
                  Manage roles by configuring role-based access control, defining what actions users can perform and which modules they can access within the CRM.
                </p>
              </div>
              <div className="d-flex align-items-center gap-2">

                <div className=" position-relative mt-0 mb-0 mt-md-0">
                  <input

                    type="search"
                    className="form-control "
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      fontSize: "12px",
                      paddingLeft: "36px",
                      height: "37px",
                    }}
                  />
                  <i
                    class="fa-solid fa-magnifying-glass"
                    style={{
                      position: "absolute",
                      marginTop: "-26px",
                      zIndex: 1,
                      marginLeft: "13px",
                    }}
                  ></i>
                </div>
                <button
                  className="btn btn-default-outline"
                  onClick={() => {
                    if (!roleGrant?.isCreate) {
                      toast.warning("You are not authorized for this action!")
                      return
                    }
                    reset();
                    setEditIndex(null);
                    setShowModal(true);
                    setIsGlobalMasking(true);
                  }}
                >
                  + Add Role
                </button>
              </div>
            </div>

              <>
                <TableComponent
                  data={filteredRoles}
                  columns={["role_title", "description"]}
                  columnLabels={{
                    role_title: "Role Name",
                    description: "Description",
                  }}
                  showDeleteModal={showDeleteModal}
                  onDeleteClose={() => setShowDeleteModal(false)}
                  onDeleteConfirm={confirmDelete}
                  loading={isDeleting || isLoading}
                  actions={(row, index) =>
                    row.is_customized === "1" ? (
                      <>
                        <Button title="Edit" size="sm" variant="outline-primary"
                          // className="btn btn-sm btn-outline-info"
                          onClick={() => {
                            if (!roleGrant?.isEdit) {
                              toast.warning("You are not authorized for this action!")
                              return
                            }
                            handleEdit(index)
                          }}
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button size="sm" title="Delete" variant="outline-danger"
                          onClick={() => {
                            if (!roleGrant?.isDelete) {
                              toast.warning("You are not authorized for this action!")
                              return
                            }
                            setDeleteIndex(index);
                            setShowDeleteModal(true);
                          }}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                        <Button size="sm" title="Permission setting" variant="outline-secondary"
                          onClick={() => {
                            if (!roleGrant?.isDelete) {
                              toast.warning("You are not authorized for this action!")
                              return
                            }
                            navigate('/rolePermissions', {
                              state: {
                                role_id: row.id
                              }
                            })
                          }}
                        >
                          <i className="bi bi-gear"></i>
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button title="Edit" size="sm" variant="outline-primary"
                          // className="btn btn-sm btn-outline-info"
                          onClick={() => {
                            if (!roleGrant?.isEdit) {
                              toast.warning("You are not authorized for this action!")
                              return
                            }
                            handleEdit(index)
                          }}
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button size="sm" title="Permission setting" variant="outline-secondary"
                          onClick={() => {
                            navigate('/rolePermissions', {
                              state: {
                                role_id: row.id
                              }
                            })
                          }}
                        >
                          <i className="bi bi-gear"></i>
                        </Button>
                      </>
                    )
                  }
                />
              </>
            
          </div>
          :
          <AuthorizationCard />
      }
      {/* style={{ background: "rgba(0, 0, 0, 0.55)" }} // The classic black fade */}

      {
        showModal && (
          <div
            className="modal d-block"
            tabIndex="-1"
            style={{ background: "rgba(0, 0, 0, 0.6)" }}
          >
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "480px" }}>
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px" }}>
                <div className="modal-header border-0 pb-0 pt-4 px-4">
                  <h5 className="modal-title fw-semibold text-dark" style={{ letterSpacing: "-0.02em" }}>
                    {editIndex !== null ? "Edit Role" : "Create New Role"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close shadow-none"
                    style={{ fontSize: "0.8rem" }}
                    onClick={() => {
                      setShowModal(false);
                      reset();
                      setCurrRoleId(null)
                      setEditIndex(null);
                    }}
                  ></button>
                </div>
                <hr className="mx-4 opacity-5" />

                <form className="form-container" onSubmit={handleSubmit(onSubmit)}>
                  <div className="modal-body px-4 pt-2">
                    {/* Role Name */}
                    <div className="mb-3 form-group position-relative">
                      <label className="form-label small fw-medium text-secondary mb-1">
                        Role Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. System Administrator"
                        disabled={
                          editIndex !== null &&
                          roles[editIndex]?.role_title === "Admin"
                        }
                        className={`form-control shadow-sm border-light-subtle ${errors.roleName ? 'is-invalid' : ''}`}
                        style={{
                          padding: "0.6rem 0.75rem",
                          borderRadius: "8px",
                          fontSize: "0.95rem",
                          backgroundColor:
                            editIndex !== null &&
                              roles[editIndex]?.role_title === "Admin"
                              ? "#e9ecef"
                              : ""
                        }}
                        {...register("roleName", { required: "Role name is required" })}
                      />
                    </div>

                    {/* Description */}
                    <div className="mb-4 form-group position-relative">
                      <label className="form-label small fw-medium text-secondary mb-1">
                        Description <span className="text-danger">*</span>
                      </label>
                      <textarea
                        placeholder="Briefly describe the responsibilities..."
                        className={`form-control shadow-sm border-light-subtle ${errors.description ? 'is-invalid' : ''}`}
                        rows="2"
                        style={{ borderRadius: "8px", fontSize: "0.95rem", resize: "none" }}
                        {...register("description", { required: "Description is required" })}
                      ></textarea>
                    </div>

                    <div className="d-flex  gap-2">
                      <p className=" fw-bold text-uppercase text-muted mb-3" style={{ fontSize: '10px', letterSpacing: '1px' }}>
                        Number Masking
                      </p>
                      <Tooltip
                        placement="right"
                        arrow
                        title={`Enable number masking for the selected module to protect user privacy.`}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              fontSize: "0.7rem",
                              backgroundColor: "#7a3a5e",
                              borderRadius: "6px",
                              padding: "6px 10px",
                            },
                          },
                          arrow: { sx: { color: "#7a3a5e" } },
                        }}
                      >
                        <div className="text-muted" style={{ cursor: 'pointer', display: 'flex' }}>
                          <IconInfoCircle size={14} stroke={2} />
                        </div>
                      </Tooltip>
                    </div>

                    <div className="mb-2 form-group position-relative" >

                      <div className="row g-2">
                        <div className="d-flex align-items-center justify-content-between p-3"
                          style={{
                            backgroundColor: "#f8f9fa",
                            borderRadius: "8px",
                            border: "1px solid #f1f3f5"
                          }}>
                          <span className="small fw-medium text-dark">
                            Number Masking
                          </span>

                          <div className=" form-check form-switch p-0 m-0">
                            <input
                              className=" form-check-input shadow-none"
                              type="checkbox"
                              role="switch"
                              checked={isGlobalMasking}
                              onChange={(e) => { setIsGlobalMasking(e.target.checked) }}
                              style={{ width: "2.5em", height: "1.2em", cursor: "pointer" }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer border-0 pb-4 px-4 pt-3">
                    <button
                      type="button"
                      className="btn btn-default-outline fw-medium"
                      disabled={isSubmitting || isUpdating}
                      onClick={() => {
                        setShowModal(false);
                        reset();
                        setCurrRoleId(null)
                        setEditIndex(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-default px-4 py-2 "
                      type="submit"
                      style={{ borderRadius: "8px", fontWeight: "500", fontSize: "0.9rem" }}
                      disabled={isSubmitting || isUpdating}
                    >
                      {(isSubmitting || isUpdating) ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          {editIndex !== null ? "Saving..." : "Creating..."}
                        </>
                      ) : (
                        editIndex !== null ? "Save Changes" : "Create Role"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};


